import React from 'react';

import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

type ProgressReportFieldsProps = {
  fields: string[];
  labelsByField: Record<string, string>;
  values: Record<string, string>;
  writableByField: Record<string, boolean>;
  onChange: (field: string, value: string) => void;
  createMode?: boolean;
};

export const PROGRESS_REPORT_REQUIRED_FIELDS = [
  'client_name',
  'case_number',
  'icms_provider_name',
  'court_house',
  'court_address',
  'icd_10_codes',
  'conditional_release_date',
  'court_date',
  'next_requested_court_date',
  'housing',
  'mental_health',
  'body_of_letter_introduction',
  'probation',
  'substance_abuse',
  'other',
  'recommendation',
  'medication',
  'client_current_condition',
] as const;

const LARGE_TEXT_FIELDS = new Set([
  'medication',
  'body_of_letter_introduction',
  'housing',
  'mental_health',
  'mental_health_medication_compliance',
  'probation',
  'substance_abuse',
  'recommendation',
  'contact_information',
  'additional_features',
  'other',
]);

const DATE_FIELDS = new Set([
  'date',
  'conditional_release_date',
  'court_date',
  'next_requested_court_date',
]);

const AUDIT_FIELDS = new Set(['created_by', 'updated_by']);

const RECOMMENDATIONS = [
  { value: 'recommendation_residential_sud_tx', label: 'Residential SUD Tx' },
  { value: 'recommendation_outpatient_sud_tx', label: 'Outpatient SUD Tx' },
  { value: 'recommendation_anger_management', label: 'Anger Management Classes' },
  { value: 'recommendation_domestic_violence', label: 'Domestic Violence Classes' },
  { value: 'recommendation_lai', label: 'LAI' },
  { value: 'recommendation_other', label: 'Other' },
];

const selectClassName =
  'h-9 w-full rounded-[var(--border-radius-medium)] border border-[var(--neutral-gray-60)] bg-white px-2 text-sm text-[var(--neutral-gray-120)] disabled:bg-[var(--neutral-gray-20)]';

const splitSelections = (value: string): string[] =>
  String(value || '')
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

export const ProgressReportFields = ({
  fields,
  labelsByField,
  values,
  writableByField,
  onChange,
  createMode = false,
}: ProgressReportFieldsProps) => {
  const visibleFields = fields.filter((field) => field !== 'id' && !AUDIT_FIELDS.has(field));

  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
      {visibleFields.map((field) => {
        const label = labelsByField[field] || field;
        const value = values[field] ?? '';
        const writable = !!writableByField[field];
        const disabled = !writable;
        const inputId = `${createMode ? 'create' : 'edit'}-progress-report-${field}`;

        if (field === 'appearance') {
          return (
            <div key={field} className="flex flex-col gap-1.5">
              <label htmlFor={inputId} className="text-xs font-medium text-[var(--neutral-gray-100)]">{label}</label>
              <select id={inputId} value={value} disabled className={selectClassName}>
                <option value="">--</option>
                <option value="appearance">Appearance</option>
                <option value="non_appearance">Non-Appearance</option>
                <option value="virtual_appearance">Virtual Appearance</option>
              </select>
              <span className="text-xs text-[var(--neutral-gray-80)]">Calculated from the latest legal hearing and client condition.</span>
            </div>
          );
        }

        if (field === 'client_current_condition') {
          return (
            <fieldset key={field} className="flex flex-col gap-2 md:col-span-2 xl:col-span-4">
              <legend className="text-xs font-medium text-[var(--neutral-gray-100)]">
                Since the last progress report, overall, the client has been doing <span className="text-[var(--semantic-error)]">*</span>
              </legend>
              <div className="flex flex-wrap gap-5 text-sm text-[var(--neutral-gray-120)]">
                {[
                  ['well', 'Well'],
                  ['not_well', 'Not Well'],
                  ['graduation_encouragement', 'Graduation/Encouragement (client should appear)'],
                ].map(([optionValue, optionLabel]) => (
                  <label key={optionValue} className="flex items-center gap-1.5">
                    <input
                      id={`${inputId}-${optionValue}`}
                      type="radio"
                      name={`${createMode ? 'create' : 'edit'}-progress-report-condition`}
                      value={optionValue}
                      checked={value === optionValue}
                      disabled={disabled}
                      onChange={() => onChange(field, optionValue)}
                    />
                    {optionLabel}
                  </label>
                ))}
              </div>
            </fieldset>
          );
        }

        if (field === 'recommendation_selection') {
          const selected = splitSelections(value);
          return (
            <fieldset key={field} className="flex flex-col gap-2 md:col-span-2 xl:col-span-2">
              <legend className="text-xs font-medium text-[var(--neutral-gray-100)]">Recommendation Selections</legend>
              <div className="grid grid-cols-1 gap-2 text-sm text-[var(--neutral-gray-120)] sm:grid-cols-2">
                {RECOMMENDATIONS.map((option) => (
                  <label key={option.value} className="flex items-center gap-1.5">
                    <input
                      id={`${inputId}-${option.value}`}
                      type="checkbox"
                      checked={selected.includes(option.value)}
                      disabled={disabled}
                      onChange={(event) => {
                        const next = new Set(selected);
                        if (event.target.checked) {
                          next.add(option.value);
                        }
                        if (!event.target.checked) {
                          next.delete(option.value);
                        }
                        onChange(field, Array.from(next).join(', '));
                      }}
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </fieldset>
          );
        }

        if (field === 'probation_status') {
          return (
            <div key={field} className="flex flex-col gap-1.5">
              <label htmlFor={inputId} className="text-xs font-medium text-[var(--neutral-gray-100)]">Probation Status</label>
              <select id={inputId} value={value} disabled={disabled} onChange={(event) => onChange(field, event.target.value)} className={selectClassName}>
                <option value="">--Select an option--</option>
                <option value="doing_well">Doing well</option>
                <option value="not_doing_well">Not doing well</option>
              </select>
            </div>
          );
        }

        if (field === 'defense_attorney') {
          return (
            <div key={field} className="flex flex-col gap-1.5">
              <label htmlFor={inputId} className="text-xs font-medium text-[var(--neutral-gray-100)]">{label}</label>
              <select id={inputId} value={value} disabled={disabled} onChange={(event) => onChange(field, event.target.value)} className={selectClassName}>
                <option value="">--Select an option--</option>
                <option value="Public Defender">Public Defender</option>
                <option value="Alternative Public Defender">Alternative Public Defender</option>
                <option value="Independent Defense Counsel Office (IDCO)">Independent Defense Counsel Office (IDCO)</option>
                <option value="Private Attorney">Private Attorney</option>
              </select>
            </div>
          );
        }

        if (field === 'is_draft' || field === 'completed') {
          const isProviderSubmitted = field === 'is_draft';
          return (
            <div key={field} className="flex flex-col gap-1.5">
              <label htmlFor={inputId} className="text-xs font-medium text-[var(--neutral-gray-100)]">{label}</label>
              <select id={inputId} value={value} disabled={disabled} onChange={(event) => onChange(field, event.target.value)} className={selectClassName}>
                <option value="">--Select an option--</option>
                <option value={isProviderSubmitted ? '0' : '1'}>Yes</option>
                <option value={isProviderSubmitted ? '1' : '0'}>No</option>
              </select>
            </div>
          );
        }

        if (field === 'amended') {
          return (
            <label key={field} className="flex min-h-9 items-center gap-2 self-end text-sm text-[var(--neutral-gray-120)]">
              <input id={inputId} type="checkbox" checked={value === '1'} disabled={disabled} onChange={(event) => onChange(field, event.target.checked ? '1' : '0')} />
              Amended
            </label>
          );
        }

        if (LARGE_TEXT_FIELDS.has(field)) {
          return (
            <div key={field} className="flex flex-col gap-1.5 md:col-span-2 xl:col-span-4">
              <label htmlFor={inputId} className="text-xs font-medium text-[var(--neutral-gray-100)]">{label}</label>
              <Textarea id={inputId} value={value} disabled={disabled} rows={4} className="min-h-[96px]" onChange={(event) => onChange(field, event.target.value)} />
            </div>
          );
        }

        return (
          <div key={field} className="flex flex-col gap-1.5">
            <label htmlFor={inputId} className="text-xs font-medium text-[var(--neutral-gray-100)]">{label}</label>
            <Input
              id={inputId}
              type={DATE_FIELDS.has(field) ? 'datetime-local' : 'text'}
              value={value}
              disabled={disabled}
              onChange={(event) => onChange(field, event.target.value)}
            />
          </div>
        );
      })}
    </div>
  );
};
